import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getMsalInstance, loginRequest } from '@/lib/msal'
import { API_BASE_URL } from '@/lib/constants'

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const msalInstance = getMsalInstance()

    if (msalInstance) {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          })
          config.headers.Authorization = `Bearer ${response.idToken}`
        } catch (error) {
          console.error('[API] Token acquisition failed:', error)
          // Token acquisition failed, proceed without token
          // The backend will handle unauthorized requests
        }
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status

      // Handle specific status codes
      if (status === 401) {
        // Unauthorized - might need to re-authenticate
        console.error('[API] Unauthorized request')
        // Could trigger re-authentication flow here
      } else if (status === 403) {
        console.error('[API] Forbidden - insufficient permissions')
      } else if (status === 404) {
        console.error('[API] Resource not found')
      } else if (status >= 500) {
        console.error('[API] Server error:', status)
      }
    } else if (error.request) {
      console.error('[API] No response received:', error.message)
    } else {
      console.error('[API] Request error:', error.message)
    }

    return Promise.reject(error)
  }
)

export default apiClient

// Helper function to handle API errors
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string }>
    if (axiosError.response?.data?.detail) {
      return axiosError.response.data.detail
    }
    if (axiosError.message) {
      return axiosError.message
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}
